import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagicWand, Trash } from "@phosphor-icons/react";

const MAX_LBS = 100;

export function RateTableEditor({ company, open, onOpenChange, onSaved }) {
  const [prices, setPrices] = useState({});
  const [currency, setCurrency] = useState("JMD");
  const [base, setBase] = useState("");
  const [inc, setInc] = useState("");
  const [saving, setSaving] = useState(false);
  const sym = currency === "USD" ? "US$" : "J$";

  // Re-seed from the latest company data each time the dialog opens
  useEffect(() => {
    if (!open) return;
    const map = {};
    (company.rate_table || []).forEach((t) => {
      map[t.lbs] = String(t.price);
    });
    setPrices(map);
    setCurrency(company.per_lb_rate_currency || "JMD");
    // eslint-disable-next-line
  }, [open]);

  const generate = () => {
    const b = parseFloat(base);
    const i = parseFloat(inc);
    if (isNaN(b) || b < 0) {
      toast.error("Enter a valid 1 lb base price");
      return;
    }
    if (isNaN(i) || i < 0) {
      toast.error("Enter a valid per-lb increase (use 0 for a flat table)");
      return;
    }
    const map = {};
    for (let w = 1; w <= MAX_LBS; w++) {
      map[w] = String(Math.round((b + (w - 1) * i) * 100) / 100);
    }
    setPrices(map);
    toast.success(`Generated all ${MAX_LBS} tiers — fine-tune any row, then Save`);
  };

  const clearAll = () => setPrices({});

  const setCount = Object.values(prices).filter((v) => v !== "" && !isNaN(parseFloat(v))).length;

  const save = async () => {
    const rates = [];
    for (let w = 1; w <= MAX_LBS; w++) {
      const v = prices[w];
      if (v === undefined || v === "") continue;
      const n = parseFloat(v);
      if (isNaN(n) || n < 0) {
        toast.error(`Invalid price for ${w} lb`);
        return;
      }
      rates.push({ lbs: w, price: n });
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/companies/${company.id}/rate-table`, { rates, currency });
      onSaved(data);
      onOpenChange(false);
      toast.success(
        rates.length
          ? `Rate table saved — ${rates.length} tier${rates.length === 1 ? "" : "s"} now visible to your customers`
          : "Rate table cleared"
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save rate table");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[95vw] h-[88vh] flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="rate-table-editor"
      >
        {/* Header */}
        <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          <DialogTitle className="text-lg font-semibold" style={{ fontFamily: "Work Sans" }}>
            Shipping rate table
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Set a price for each package weight from 1 to {MAX_LBS} lbs. Leave a row blank to exclude that tier.
            Customers see this table on their dashboard and pickup forms.
          </DialogDescription>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Currency</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="h-8 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-sm"
              data-testid="rate-table-currency-select"
            >
              <option value="JMD">JMD</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        {/* Auto-fill bar */}
        <div className="px-5 sm:px-6 py-3 bg-zinc-50 dark:bg-zinc-900/60 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          <div className="flex flex-wrap items-end gap-2 sm:gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">1 lb price</div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={base}
                onChange={(e) => setBase(e.target.value)}
                placeholder="650"
                className="no-spinner w-24 h-8 bg-white dark:bg-zinc-900"
                data-testid="rate-autofill-base"
              />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">+ per extra lb</div>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={inc}
                onChange={(e) => setInc(e.target.value)}
                placeholder="300"
                className="no-spinner w-24 h-8 bg-white dark:bg-zinc-900"
                data-testid="rate-autofill-increment"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={generate}
              className="h-8 bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="rate-autofill-generate"
            >
              <MagicWand size={13} weight="bold" className="mr-1.5" />
              Generate 1–{MAX_LBS} lbs
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clearAll}
              className="h-8 text-zinc-500 hover:text-red-600"
              data-testid="rate-table-clear-btn"
            >
              <Trash size={13} className="mr-1.5" />
              Clear all
            </Button>
          </div>
        </div>

        {/* Tier grid */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
            {Array.from({ length: MAX_LBS }, (_, idx) => idx + 1).map((w) => (
              <div key={w} className="flex items-center gap-2">
                <div className="w-14 text-right text-xs font-semibold text-zinc-600 dark:text-zinc-400 shrink-0">
                  {w} lb
                </div>
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-zinc-500 pointer-events-none">
                    {sym}
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={prices[w] ?? ""}
                    onChange={(e) => setPrices((p) => ({ ...p, [w]: e.target.value }))}
                    placeholder="—"
                    className="no-spinner h-8 !pl-11 text-sm"
                    data-testid={`rate-input-${w}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between shrink-0 bg-white dark:bg-zinc-900">
          <div className="text-xs text-zinc-500" data-testid="rate-table-set-count">
            <b>{setCount}</b> of {MAX_LBS} tiers set
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="rate-table-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="rate-table-save-btn"
            >
              {saving ? "Saving…" : "Save rate table"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
